import React, { useEffect, useRef } from 'react';
import { arcPath, EYE_H, EYE_W, EYES, HEAD_PATH } from './Mascot';

export interface AgentGlyphProps {
  /** Closed-eye arcs instead of open eyes. */
  asleep?: boolean;
  /** Rendered width and height in px. Omit to size it from `className`. */
  size?: number;
  className?: string;
}

const BLINK_EVERY_MS = 4800;
const BLINK: Keyframe[] = [
  { transform: 'scaleY(1)' },
  { transform: 'scaleY(0.1)', offset: 0.5 },
  { transform: 'scaleY(1)' },
];

/**
 * The agent face as a line ICON: the Mascot's own head and eyes, drawn in
 * `currentColor` with no fill — so it sits among the other monochrome icons and
 * takes their colour (muted, hover, white when active). Awake it blinks now and
 * then; asleep it shows the closed-eye arcs. For a face in an agent's own tone,
 * use <Mascot> / <AgentFace>.
 */
export const AgentGlyph: React.FC<AgentGlyphProps> = ({ asleep, size, className }) => {
  const eyes = useRef<SVGGElement>(null);

  useEffect(() => {
    const el = eyes.current;
    if (asleep || !el?.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    let anim: Animation | undefined;
    const blink = () => { anim = el.animate(BLINK, { duration: 180, easing: 'ease-in-out' }); };
    const timer = setInterval(blink, BLINK_EVERY_MS);
    return () => { clearInterval(timer); anim?.cancel(); };
  }, [asleep]);

  return (
    <svg
      viewBox="-10 -10 220 220"
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      aria-hidden
      focusable="false"
    >
      <path d={HEAD_PATH} strokeWidth={17} strokeLinejoin="round" />
      {asleep ? (
        EYES.map((e) => <path key={e.cx} d={arcPath(e)} strokeWidth={15} strokeLinecap="round" />)
      ) : (
        <g ref={eyes} style={{ transformBox: 'fill-box', transformOrigin: 'center' }} fill="currentColor" stroke="none">
          {EYES.map((e) => (
            <rect
              key={e.cx}
              x={e.cx - EYE_W / 2}
              y={e.cy - EYE_H / 2}
              width={EYE_W}
              height={EYE_H}
              rx={EYE_W / 2}
              transform={`rotate(${e.rot} ${e.cx} ${e.cy})`}
            />
          ))}
        </g>
      )}
    </svg>
  );
};
